"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalDate = exports.constructSymbol = void 0;
exports.toLocalDate = toLocalDate;
const tslib_1 = require("tslib");
const moment_timezone_1 = tslib_1.__importDefault(require("moment-timezone"));
const locale_1 = require("./locale");
const utils_1 = require("./utils");
/** @internal */
exports.constructSymbol = Symbol();
class LocalDate {
    get __type__() {
        return this._type_;
    }
    get options() {
        return {
            format: this._format,
            locale: this._locale
        };
    }
    get moment() {
        return (0, utils_1.getMoment)(this.__originalDate__, this.options);
    }
    new(date) {
        return this.createNew(date, this.options);
    }
    createNew(date, options) {
        const Constructor = this.constructor;
        return new Constructor(exports.constructSymbol, date, options);
    }
    static now(...[timezone]) {
        const now = timezone ? (0, moment_timezone_1.default)().tz(timezone) : (0, moment_timezone_1.default)();
        return toLocalDate(now.startOf('day'));
    }
    static from(year, month, day) {
        const now = LocalDate.now('');
        return now
            .setYear(year)
            .setMonth(month !== null && month !== void 0 ? month : 0)
            .setDate(day !== null && day !== void 0 ? day : 1);
    }
    constructor(_symbol, date, options) {
        Object.defineProperty(this, "_type_", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'date'
        });
        Object.defineProperty(this, "__originalDate__", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_locale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_format", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.__originalDate__ = date;
        this._locale = options === null || options === void 0 ? void 0 : options.locale;
        this._format = options === null || options === void 0 ? void 0 : options.format;
    }
    subDays(days) {
        const newDate = this.moment.subtract(days, 'days');
        return this.new(newDate);
    }
    addDays(days) {
        const newDate = this.moment.add(days, 'days');
        return this.new(newDate);
    }
    addWeeks(weeks) {
        const newDate = this.moment.add(weeks, 'weeks');
        return this.new(newDate);
    }
    addMonths(months) {
        const newDate = this.moment.add(months, 'months');
        return this.new(newDate);
    }
    addYears(years) {
        const newDate = this.moment.add(years, 'years');
        return this.new(newDate);
    }
    subWeeks(weeks) {
        const newDate = this.moment.subtract(weeks, 'weeks');
        return this.new(newDate);
    }
    subMonths(months) {
        const newDate = this.moment.subtract(months, 'months');
        return this.new(newDate);
    }
    subQuarters(quarter) {
        const newDate = this.moment.subtract(quarter, 'quarters');
        return this.new(newDate);
    }
    subYears(years) {
        const newDate = this.moment.subtract(years, 'years');
        return this.new(newDate);
    }
    setDay(day) {
        const newDate = this.moment.day(day);
        return this.new(newDate);
    }
    setDate(date) {
        const newDate = this.moment.date(date);
        return this.new(newDate);
    }
    /**
     * The ISO week counter starts the first thursday of the year;
     * If the first day of the year is saturday, the first thursday will be in the next week.
     *
     * @see https://stackoverflow.com/a/32187855/1138860
     */
    setIsoWeek(week) {
        const newDate = this.moment.isoWeek(week);
        return this.new(newDate);
    }
    setWeek(week) {
        const newDate = this.moment.week(week);
        return this.new(newDate);
    }
    setMonth(month) {
        const newDate = this.moment.month(month);
        return this.new(newDate);
    }
    setYear(year) {
        const newDate = this.moment.year(year);
        return this.new(newDate);
    }
    startOf(unit) {
        const normalizedUnit = (0, utils_1.normalizeUnit)(unit);
        const newDate = this.moment.startOf(normalizedUnit);
        return this.new(newDate);
    }
    endOf(unit) {
        const normalizedUnit = (0, utils_1.normalizeUnit)(unit);
        const newDate = this.moment.endOf(normalizedUnit);
        return this.new(newDate);
    }
    isSame(otherDate) {
        const otherDateCleaned = (0, utils_1.getMoment)(otherDate, this.options);
        return this.moment.isSame(otherDateCleaned);
    }
    isSameDay(otherDate) {
        const otherDateCleaned = (0, utils_1.getMoment)(otherDate, this.options);
        return this.moment.isSame(otherDateCleaned, 'day');
    }
    isToday(timezone) {
        return this.isSameDay(LocalDate.now(timezone));
    }
    isTomorrow(timezone) {
        return this.diff(LocalDate.now(timezone), 'day') === 1;
    }
    isYesterday(timezone) {
        return this.diff(LocalDate.now(timezone), 'day') === -1;
    }
    isFuture(timezone) {
        return this.getTime() > LocalDate.now(timezone).getTime();
    }
    isThisWeek(timezone) {
        const now = LocalDate.now(timezone).startOf('week');
        return this.diff(now, 'week') === 0;
    }
    isNextWeek(timezone) {
        const nextWeek = LocalDate.now(timezone).addWeeks(1).startOf('week');
        return this.diff(nextWeek, 'week') === 0;
    }
    isLastWeek(timezone) {
        const lastWeek = LocalDate.now(timezone).subWeeks(1);
        return this.diff(lastWeek, 'week') === 0;
    }
    isThisMonth(timezone) {
        const thisMonth = LocalDate.now(timezone);
        return this.diff(thisMonth, 'month') === 0;
    }
    isNextMonth(timezone) {
        const nextMonth = LocalDate.now(timezone).addMonths(1);
        return this.diff(nextMonth, 'month') === 0;
    }
    isLastMonth(timezone) {
        const lastMonth = LocalDate.now(timezone).subMonths(1);
        return this.diff(lastMonth, 'month') === 0;
    }
    isBefore(otherDate) {
        const otherDateCleaned = (0, utils_1.getMoment)(otherDate, this.options);
        return this.moment.isBefore(otherDateCleaned);
    }
    isAfter(otherDate) {
        const otherDateCleaned = (0, utils_1.getMoment)(otherDate, this.options);
        return this.moment.isAfter(otherDateCleaned);
    }
    isBetween(dateLeft, dateRight, unit, inclusivity) {
        const dateLeftCleaned = (0, utils_1.getMoment)(dateLeft, this.options);
        const dateRightCleaned = (0, utils_1.getMoment)(dateRight, this.options);
        return this.moment.isBetween(dateLeftCleaned, dateRightCleaned, unit, inclusivity);
    }
    /**
     * Replicate 'date-fns'.isWithinRange
     */
    isWithinRange(dateLeft, dateRight, unit) {
        return this.isBetween(dateLeft, dateRight, unit, '[]');
    }
    isValid() {
        const { suppressDeprecationWarnings } = moment_timezone_1.default;
        // NOTE:
        // whenever there is an invalid format inside moment, it will console.warn. we definitely want that as default case.
        // however, here we just want to simply check if the date is valid, so there is no need to print a warning,
        // as the method will return false either way, if the date is invalid.
        // also @ts-ignore is needed, because the typings of moment declare it as readonly while it's really not.
        // @ts-ignore
        moment_timezone_1.default.suppressDeprecationWarnings = true;
        const isValid = this.moment.isValid();
        // @ts-ignore
        moment_timezone_1.default.suppressDeprecationWarnings = suppressDeprecationWarnings;
        return isValid;
    }
    diff(otherDate, format, precise) {
        const otherDateCleaned = (0, utils_1.getMoment)(otherDate, this.options);
        return this.moment.diff(otherDateCleaned, format, precise);
    }
    getCalendarWeekYear() {
        return this.moment.isoWeekYear();
    }
    getCalendarWeek() {
        return this.moment.isoWeek();
    }
    getWeekday() {
        return this.moment.format('dddd');
    }
    fromNow(withoutSuffix) {
        return this.moment.fromNow(withoutSuffix);
    }
    format(format) {
        return this.moment.format(format);
    }
    getDaysInMonth() {
        return this.moment.daysInMonth();
    }
    locale(locale) {
        if (locale) {
            (0, locale_1.setMomentLocale)(locale.substring(0, 2).toLowerCase());
            this._locale = locale;
        }
        return this.new(this.moment);
    }
    /**
     * @deprecated
     * Please use `toZonedDateTimeWithCustomFormat` instead
     */
    setFormat(format) {
        this._format = format;
        return this.new(this.moment);
    }
    /**
     * Native date get-methods
     */
    /** Gets the time value in milliseconds. */
    getTime() {
        return parseInt(this.moment.format('x'), 10);
    }
    getFullYear() {
        return this.moment.get('year');
    }
    getMonth() {
        return this.moment.get('month');
    }
    getDate() {
        return this.moment.get('date');
    }
    getDay() {
        return this.moment.get('day');
    }
    valueOf() {
        return this.getTime();
    }
    toLocaleString(locales, options) {
        return this.toDate().toLocaleString(locales, options);
    }
    formatForServer() {
        return this.format('YYYY-MM-DD');
    }
    toString() {
        return this.formatForServer();
    }
    toJSON() {
        return this.toString();
    }
    toDate() {
        return (0, moment_timezone_1.default)(this.toString()).toDate();
    }
    toMonthString() {
        return this.format('YYYY-MM');
    }
    /**
     * @deprecated
     * Please avoid using this method -
     * it's only meant for usage in the legacy part of the application
     */
    toMoment() {
        return this.moment;
    }
    [Symbol.toPrimitive](hint) {
        switch (hint) {
            case 'number':
                return this.getTime();
            default:
            case 'default':
            case 'string':
                return this.toString();
        }
    }
}
exports.LocalDate = LocalDate;
function toLocalDate(date) {
    return date ? new LocalDate(exports.constructSymbol, date) : undefined;
}
